import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';

import { apiV2Public, getData, postData, putData } from '@/lib/api/client-v2';
import { queryKeys } from '@/lib/query-keys';
import type { LocalisationVehiculeValues } from '@/schemas/localisation-vehicule';
import type { AccimotoLink, AccimotoPickupLocation, AccimotoProposal } from '@/types/api/accimoto';
import { PUBLIC_ENDPOINTS } from '@/types/api/endpoints';

/*
 * Parcours client Accimoto ouvert depuis le mail : pages publiques, sans connexion (Gravitee et le
 * MFA rendaient le parcours impossible depuis un mail). Le lien signé par ABS
 * (`/accimoto/{sinistre}/{page}?expires=…&signature=…`) est rejoué tel quel vers le Pass, qui le
 * vérifie avant toute lecture ou écriture.
 */

/** Lien signé de la page courante. */
export const useAccimotoLink = (): AccimotoLink => {
    const { accidentId = '' } = useParams();
    const [searchParams] = useSearchParams();

    return { accidentId, expires: searchParams.get('expires'), signature: searchParams.get('signature') };
};

const signedParams = ({ expires, signature }: AccimotoLink) => ({ expires, signature });

export const toPickupLocationBody = (form: LocalisationVehiculeValues): AccimotoPickupLocation => ({
    name: form.name.trim(),
    address: form.address.trim(),
    postal_code: form.postal_code.trim(),
    town: form.town.trim(),
});

/** Relue à chaque ouverture : le client vient souvent de répondre depuis le mail. */
export const useAccimotoProposal = (link: AccimotoLink, enabled = true) =>
    useQuery({
        queryKey: queryKeys.accimotoProposal(link.accidentId),
        queryFn: () => getData<AccimotoProposal>(PUBLIC_ENDPOINTS.accimoto(link.accidentId), signedParams(link), apiV2Public),
        refetchOnMount: 'always',
        retry: false,
        enabled,
    });

/** Réponse du client (« J'accepte » / « Je refuse ») ; le Pass rend l'état qui en résulte. */
export const useAnswerAccimoto = (link: AccimotoLink) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (answer: 'accept' | 'refuse') =>
            postData<AccimotoProposal>(
                PUBLIC_ENDPOINTS.accimotoAnswer(link.accidentId, answer),
                undefined,
                { params: signedParams(link) },
                apiV2Public,
            ),
        onSuccess: (proposal) => queryClient.setQueryData(queryKeys.accimotoProposal(link.accidentId), proposal),
    });
};

/** Saisie du lieu d'enlèvement, re-saisie permise. */
export const useUpdateAccimotoPickupLocation = (link: AccimotoLink) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (form: LocalisationVehiculeValues) =>
            putData<AccimotoProposal>(
                PUBLIC_ENDPOINTS.accimotoPickupLocation(link.accidentId),
                toPickupLocationBody(form),
                { params: signedParams(link) },
                apiV2Public,
            ),
        onSuccess: (proposal) => queryClient.setQueryData(queryKeys.accimotoProposal(link.accidentId), proposal),
        // Refus métier (délai de 24 h dépassé…) : relire l'état pour que la page l'affiche.
        onError: () => queryClient.invalidateQueries({ queryKey: queryKeys.accimotoProposal(link.accidentId) }),
    });
};
